import { useUpdateEffect } from '@cian/react-utils';

import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { getQueryKey } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/internal/config';
import { useReduxDispatch } from '@/packages/redux-state/store';
import {
  jsonQueryActions,
  pageSelector,
  queryKeySelector,
  useNeedUpdateQueryKeySelector,
} from '@/packages/redux-state/store/slices/jsonQuery';

import { type TSearchOffersDesktopResponse_1 } from '@/repositories/generated/shared/search-offers/v3/search-offers-desktop';

/**
 * Обновляет queryKey для квери офферов, если требуется
 */
export const useUpdateSearchOffersQueryKeyIfNeeded = (): void => {
  const dispatch = useReduxDispatch();

  const needUpdateQueryKey = useNeedUpdateQueryKeySelector();

  const queryClient = useQueryClient();

  const reduxState = useReduxState();

  useUpdateEffect(() => {
    if (!needUpdateQueryKey) {
      return;
    }

    const state = reduxState.getState();
    const queryKey = queryKeySelector(state);
    const page = pageSelector(state);

    const [, queryKeyParam] = queryKey;
    const newQueryKeyParam = String(page);

    // Если ключ не изменился, то просто сбрасываем его к начальному состоянию
    if (queryKeyParam !== newQueryKeyParam) {
      dispatch(jsonQueryActions.setQueryKey(newQueryKeyParam));
    } else {
      const key = getQueryKey(queryKey);

      queryClient.setQueryData(key, (data: InfiniteData<TSearchOffersDesktopResponse_1>) => ({
        pages: data.pages.slice(0, 1),
        pageParams: data.pageParams.slice(0, 1),
      }));

      queryClient.refetchQueries(key);
    }

    dispatch(jsonQueryActions.setNeedUpdateQueryKey(false));
  }, [needUpdateQueryKey]);
};
